import { queryClient } from 'App';

/**
 * Matches any cached transaction list. Transaction list caches share the
 * `{ transactions, count }` shape and their query keys all contain "transaction"
 * (e.g. 'transactions', 'billing-transactions').
 */
const isTransactionQuery = (query) =>
  Array.isArray(query.queryKey) &&
  query.queryKey.some(
    (key) => typeof key === 'string' && key.toLowerCase().includes('transaction')
  );

/**
 * Live transaction update. The backend pushes a `transaction-create` over the
 * practice socket whenever a transaction is processed. We merge it straight into
 * the cached transaction lists — NO refetch / no extra backend request.
 *
 * Patient-scoped lists (a patient's billing tab, admin → patient) tag their query
 * with `meta.transactionPatientId`; we only merge updates for that patient into
 * them. View-all lists (admin "all transactions", admin → practice) carry no such
 * meta and receive every update.
 */
export default async (data) => {
  const transaction = data?.transaction;
  if (!transaction?.id) return;

  const queries = queryClient.getQueryCache().findAll({ predicate: isTransactionQuery });

  queries.forEach((query) => {
    const scopedPatientId = query.meta?.transactionPatientId;
    // Patient-scoped list: skip updates that belong to a different patient.
    if (scopedPatientId != null && String(scopedPatientId) !== String(transaction.patient_id)) {
      return;
    }

    queryClient.setQueryData(query.queryKey, (oldData) => {
      if (!oldData || !Array.isArray(oldData.transactions)) return oldData;

      const exists = oldData.transactions.some((t) => t?.id === transaction.id);
      const transactions = exists
        ? oldData.transactions.map((t) => (t?.id === transaction.id ? { ...t, ...transaction } : t))
        : [transaction, ...oldData.transactions];

      return {
        ...oldData,
        transactions,
        count: exists ? oldData.count : (oldData.count ?? 0) + 1
      };
    });
  });
};
